<template>
  <div
    class="hidden-menu-button"
    :class="{ _active: dataActive, disabled: disabled }"
    @click="handleClick"
  >
    <cv-button
      type="button"
      :kind="kind"
      :size="size"
      :disabled="disabled"
      :class="{ 'bx--btn--icon-only': iconOnly }"
    >
      <slot />
    </cv-button>
    <overflow-menu v-if="showOverflowMenu" ref="overflowMenu" :flip-menu="flipMenu">
      <template #trigger>
        <svg-icon name="Kebab16" kind="action" />
      </template>
      <slot name="overflow-menu">
        <!-- Fallback content -->
      </slot>
    </overflow-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from "vue";

// Props
interface Props {
  kind?: string;
  size?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  active?: boolean;
  flipMenu?: boolean;
}

const {
  kind = "tertiary",
  size = "small",
  iconOnly = false,
  disabled = false,
  active = false,
  flipMenu = false,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  click: [data: unknown];
}>();

// Data
const dataActive = ref(active);

// Computed
const showOverflowMenu = computed(() => {
  return dataActive.value;
});

// Methods
const handleClick = (value) => {
  if (!disabled) {
    // dataActive = true;
    emit("click", value);
  }
};

// Watch
watch(
  () => active,
  () => {
    dataActive.value = active;
  },
);
</script>

<style lang="scss">
@use "@styles/abstracts/functions" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.hidden-menu-button {
  @include carbon--type-style("productive-heading-02");
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 2rem;
  min-width: 2rem;
  width: 2rem;
  height: 2rem;
  margin: 0 $spacing-03;
  background-color: $ui-01;
  color: $text-02;
  border: 1px solid $ui-03;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background-color: tint($interactive-03, 95%);
    color: $text-02;
    border-color: $interactive-02;
    box-shadow: $shadow-01;

    .inactive-edit & {
      background-color: inherit;
    }
  }

  &:focus {
    border-color: $focus;
    outline: none;
  }

  &.selected {
    background-color: $ui-01;
    color: $text-02;
    border-color: $interactive-02;
    box-shadow: $shadow-01;

    .inactive-edit & {
      background-color: inherit;
    }
  }

  &.selected {
    &:hover {
      background-color: tint($interactive-03, 95%);
    }
  }

  &::before {
    left: -17px; // margin + border
  }

  &::after {
    right: -17px;
  }

  &.cover,
  &.end {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &.selected {
      border-color: $state-neutral;

      &:hover,
      &:focus {
        border-color: $state-neutral;
      }
    }
  }

  &.cover {
    margin-left: 0;

    &::before {
      display: none;
    }
  }

  &.end {
    margin-right: 0;

    &::after {
      display: none;
    }
  }

  &.completed {
    border: 1px solid $highlight;
    color: $support-04;
  }

  &.selected {
    border: 1px solid $ui-05;
  }

  &.disabled {
    background-color: $disabled-01;
    cursor: not-allowed;

    &:hover {
      background-color: inherit;
    }
  }

  &.submitted {
    background-color: tint($state-neutral, 90%);
    border: 1px solid tint($state-neutral, 60%);

    &.selected {
      border-color: $state-neutral;
    }
  }

  &.correct {
    background-color: tint($state-positive, 90%);
    color: $state-positive;
    border: 1px solid tint($state-positive, 60%);

    &.selected {
      border-color: $state-positive;
    }
  }

  &.wrong {
    background-color: tint($state-negative, 90%);
    color: $state-negative;
    border: 1px solid tint($state-negative, 60%);

    &.selected {
      border-color: $state-negative;
    }
  }

  &.partial-correct {
    background-color: tint($state-partial, 90%);
    color: $state-partial;
    border: 1px solid tint($state-partial, 60%);

    &.selected {
      border-color: $state-partial;
    }
  }

  &._active {
    flex-basis: 4rem;
    width: 4rem;

    .bx--btn {
      color: $text-02;
      padding-right: 1rem;

      &[disabled] {
        color: $disabled-02;
      }
    }
  }

  &.btn-add-page {
    &.disabled {
      &:hover {
        background-color: $disabled-02;
        border: none;
      }
    }

    &:hover {
      background-color: $hover-primary;

      .bx--btn {
        background-color: inherit;
      }
    }
  }

  .inactive-edit & {
    border-color: $ui-03;
  }

  .bx--btn {
    @include carbon--type-style("productive-heading-02");
    min-width: auto;
    width: inherit;
    padding: 0;
    color: $text-02;
    border: none;
    border-radius: 10px;
    white-space: nowrap;

    &[disabled] {
      + .bx--overflow-menu {
        cursor: not-allowed;

        .bx--overflow-menu__trigger {
          pointer-events: none;
          cursor: not-allowed;

          .sls-icon {
            [fill] {
              fill: $disabled-02;
            }
          }
        }
      }
    }

    &:focus,
    &:hover {
      background: none;
      box-shadow: none;
      color: inherit;
      border: none;
    }
  }

  .bx--btn--sm.cv-button {
    @include carbon--type-style("productive-heading-02");

    &:focus {
      border: none;
    }
  }

  .bx--overflow-menu {
    position: absolute;
    top: -1px; // border
    right: 0;
    width: 2rem;
    height: 2rem;

    &:hover {
      background: none;
    }

    &:focus {
      outline: none;
    }

    .bx--overflow-menu__trigger.bx--tooltip--a11y.bx--tooltip__trigger {
      &:focus {
        border: none;
      }

      &[aria-expanded="true"] {
        background-color: transparent;
      }
    }

    .sls-icon {
      width: auto;
      height: auto;
    }
  }

  .bx--tooltip__label .bx--tooltip__trigger {
    margin-left: 0;
  }
}
</style>
